// ─────────────────────────────────────────────────────
// Settings Page — Account, mirror, preferences, integrations
// ─────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { Save, User, Monitor, Bell, Shield, Palette, Wifi, Cpu, Loader2, LogOut } from "lucide-react";
import { clsx } from "clsx";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";

const SECTIONS = [
  { id: "profile", label: "Profile", icon: User },
  { id: "mirror", label: "Mirror", icon: Monitor },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "privacy", label: "Privacy", icon: Shield },
  { id: "appearance", label: "Appearance", icon: Palette },
  { id: "network", label: "Network", icon: Wifi },
  { id: "hardware", label: "Hardware", icon: Cpu },
];

function Toggle({ enabled, onToggle }: { enabled: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      className={clsx(
        "w-11 h-6 rounded-full transition-colors relative",
        enabled ? "bg-sxt-primary" : "bg-sxt-border"
      )}
    >
      <div
        className={clsx(
          "w-5 h-5 rounded-full bg-white absolute top-0.5 transition-transform",
          enabled ? "translate-x-5.5" : "translate-x-0.5"
        )}
      />
    </button>
  );
}

function SettingRow({ label, desc, children }: { label: string; desc?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-4 border-b border-sxt-border/50">
      <div>
        <p className="text-sm font-medium">{label}</p>
        {desc && <p className="text-xs text-sxt-muted mt-0.5">{desc}</p>}
      </div>
      {children}
    </div>
  );
}

export function SettingsPage() {
  const navigate = useNavigate();
  const [section, setSection] = useState("profile");
  const [notifications, setNotifications] = useState(true);
  const [mirrorWake, setMirrorWake] = useState(true);
  const [bciTracking, setBciTracking] = useState(true);
  const [analytics, setAnalytics] = useState(true);
  const [darkMode, setDarkMode] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  // Profile fields — controlled state
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [skinTone, setSkinTone] = useState("");
  const [styleArchetype, setStyleArchetype] = useState("");

  // Load profile on mount
  useEffect(() => {
    const load = async () => {
      try {
        const me = await api.getMe();
        setDisplayName(me.display_name || me.username || "");
        setEmail(me.email || "");
      } catch { /* not logged in */ }
      try {
        const profile = await api.getStyleProfile();
        setSkinTone(profile.color_season || profile.skin_tone || "");
        setStyleArchetype(profile.style_archetype || "");
      } catch { /* no profile yet */ }
      try {
        const prefs = await api.getProfile();
        if (prefs?.notification_prefs) setNotifications(prefs.notification_prefs.enabled !== false);
        if (prefs?.bci_enabled !== undefined) setBciTracking(prefs.bci_enabled);
        if (prefs?.privacy_level === "public") setAnalytics(true);
      } catch { /* no prefs yet */ }
    };
    load();
  }, []);

  const handleSaveProfile = async () => {
    setSaving(true);
    setSaveMsg("");
    try {
      await api.updateProfile({ display_name: displayName, skin_tone: skinTone, style_archetype: styleArchetype });
      setSaveMsg("Saved!");
      setTimeout(() => setSaveMsg(""), 2000);
    } catch {
      setSaveMsg("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await api.updatePreferences({
        bci_enabled: bciTracking,
        social_sharing_default: analytics,
        notification_prefs: { enabled: notifications },
      });
      setSaveMsg("Saved!");
      setTimeout(() => setSaveMsg(""), 2000);
    } catch {
      setSaveMsg("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("sxt_token");
    localStorage.removeItem("sxt_refresh_token");
    localStorage.removeItem("sxt_user_id");
    navigate("/login");
  };

  return (
    <div>
      <h2 className="text-3xl font-bold mb-8">Settings</h2>

      <div className="flex gap-6">
        {/* Sidebar Nav */}
        <div className="w-56 shrink-0">
          <nav className="space-y-1">
            {SECTIONS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setSection(id)}
                className={clsx(
                  "w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors",
                  section === id
                    ? "bg-sxt-primary/15 text-sxt-primary"
                    : "text-sxt-muted hover:text-sxt-text hover:bg-sxt-border/50"
                )}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </nav>
          <button
            onClick={handleLogout}
            className="mt-6 w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Log Out
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 bg-sxt-surface rounded-2xl border p-8 animate-fade-in">
          {section === "profile" && (
            <div>
              <h3 className="text-xl font-bold mb-6">Profile Settings</h3>
              <div className="flex items-center gap-6 mb-8">
                <div className="w-20 h-20 rounded-2xl bg-sxt-primary flex items-center justify-center text-2xl font-bold">
                  U
                </div>
                <div>
                  <button className="text-sm bg-sxt-primary/15 text-sxt-primary px-4 py-2 rounded-xl font-semibold hover:bg-sxt-primary/25 transition-colors">
                    Change Avatar
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 max-w-xl">
                <div>
                  <label className="text-xs text-sxt-muted block mb-1.5">Full Name</label>
                  <input className="w-full bg-sxt-bg border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sxt-primary" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-sxt-muted block mb-1.5">Email</label>
                  <input className="w-full bg-sxt-bg border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sxt-primary text-sxt-dim" value={email} readOnly />
                </div>
                <div>
                  <label className="text-xs text-sxt-muted block mb-1.5">Skin Tone</label>
                  <select className="w-full bg-sxt-bg border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sxt-primary" value={skinTone} onChange={(e) => setSkinTone(e.target.value)}>
                    <option value="">Select...</option>
                    <option value="Warm Autumn">Warm Autumn</option>
                    <option value="Cool Winter">Cool Winter</option>
                    <option value="Warm Spring">Warm Spring</option>
                    <option value="Cool Summer">Cool Summer</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-sxt-muted block mb-1.5">Style Archetype</label>
                  <select className="w-full bg-sxt-bg border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sxt-primary" value={styleArchetype} onChange={(e) => setStyleArchetype(e.target.value)}>
                    <option value="">Select...</option>
                    <option value="Modern Classic">Modern Classic</option>
                    <option value="Minimalist">Minimalist</option>
                    <option value="Streetwear">Streetwear</option>
                    <option value="Bohemian">Bohemian</option>
                    <option value="Avant Garde">Avant Garde</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-6">
                <button onClick={handleSaveProfile} disabled={saving}
                  className="flex items-center gap-2 bg-sxt-primary hover:bg-sxt-primary-hover text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
                {saveMsg && <span className="text-sm text-sxt-primary">{saveMsg}</span>}
              </div>
            </div>
          )}

          {section === "mirror" && (
            <div>
              <h3 className="text-xl font-bold mb-6">Mirror Settings</h3>
              <SettingRow label="Auto-Wake on Presence" desc="Turn on mirror when you're detected nearby">
                <Toggle enabled={mirrorWake} onToggle={() => setMirrorWake(!mirrorWake)} />
              </SettingRow>
              <SettingRow label="Default Screen" desc="Screen shown when mirror wakes up">
                <select className="bg-sxt-bg border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-sxt-primary">
                  <option>Home</option>
                  <option>Try On</option>
                  <option>Wardrobe</option>
                  <option>Style</option>
                </select>
              </SettingRow>
              <SettingRow label="Gesture Sensitivity" desc="Sensitivity of hand gesture recognition">
                <select className="bg-sxt-bg border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-sxt-primary">
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                </select>
              </SettingRow>
              <SettingRow label="Display Brightness" desc="Mirror display brightness level">
                <input type="range" min="0" max="100" defaultValue="80" className="w-32 accent-sxt-primary" />
              </SettingRow>
              <SettingRow label="BCI Neural Tracking" desc="Enable Muse S EEG emotion tracking">
                <Toggle enabled={bciTracking} onToggle={() => setBciTracking(!bciTracking)} />
              </SettingRow>
            </div>
          )}

          {section === "notifications" && (
            <div>
              <h3 className="text-xl font-bold mb-6">Notification Preferences</h3>
              <SettingRow label="Push Notifications" desc="Outfit suggestions, scan reminders">
                <Toggle enabled={notifications} onToggle={() => setNotifications(!notifications)} />
              </SettingRow>
              <SettingRow label="Social Mentions" desc="When someone interacts with your shared looks">
                <Toggle enabled={true} onToggle={() => {}} />
              </SettingRow>
              <SettingRow label="Style Tips" desc="Weekly AI-generated style advice">
                <Toggle enabled={true} onToggle={() => {}} />
              </SettingRow>
              <SettingRow label="Mirror Alerts" desc="Connection status, hardware warnings">
                <Toggle enabled={true} onToggle={() => {}} />
              </SettingRow>
            </div>
          )}

          {section === "privacy" && (
            <div>
              <h3 className="text-xl font-bold mb-6">Privacy & Data</h3>
              <SettingRow label="Analytics Collection" desc="Help improve SXT with usage analytics">
                <Toggle enabled={analytics} onToggle={() => setAnalytics(!analytics)} />
              </SettingRow>
              <SettingRow label="Body Scan Data" desc="Store body measurements locally only">
                <Toggle enabled={true} onToggle={() => {}} />
              </SettingRow>
              <SettingRow label="BCI Data Retention" desc="How long to keep EEG/emotion data">
                <select className="bg-sxt-bg border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-sxt-primary">
                  <option>7 days</option>
                  <option>30 days</option>
                  <option>90 days</option>
                  <option>Forever</option>
                </select>
              </SettingRow>
              <div className="mt-6 pt-4 border-t border-sxt-border/50 flex gap-4">
                <button onClick={handleSavePreferences} className="text-sm bg-sxt-primary/15 text-sxt-primary px-4 py-2 rounded-xl font-semibold hover:bg-sxt-primary/25 transition-colors">
                  Save Privacy Settings
                </button>
                <button className="text-sm text-red-400 hover:text-red-300 font-medium transition-colors">
                  Delete All Data
                </button>
              </div>
            </div>
          )}

          {section === "appearance" && (
            <div>
              <h3 className="text-xl font-bold mb-6">Appearance</h3>
              <SettingRow label="Dark Mode" desc="Always dark for that mirror aesthetic">
                <Toggle enabled={darkMode} onToggle={() => setDarkMode(!darkMode)} />
              </SettingRow>
              <SettingRow label="Accent Color" desc="Primary UI color throughout the portal">
                <div className="flex gap-2">
                  {["#6C63FF", "#00E676", "#FF5252", "#FFB74D", "#00BCD4", "#E040FB"].map((c) => (
                    <button
                      key={c}
                      className="w-7 h-7 rounded-full border-2 border-transparent hover:border-white/50 transition-colors"
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </SettingRow>
            </div>
          )}

          {section === "network" && (
            <div>
              <h3 className="text-xl font-bold mb-6">Network</h3>
              <SettingRow label="API Endpoint" desc="SXT Platform backend URL">
                <input className="bg-sxt-bg border rounded-lg px-3 py-1.5 text-sm w-64 font-mono focus:outline-none focus:border-sxt-primary" defaultValue="http://localhost:8000" />
              </SettingRow>
              <SettingRow label="MQTT Broker" desc="Event bus broker address">
                <input className="bg-sxt-bg border rounded-lg px-3 py-1.5 text-sm w-64 font-mono focus:outline-none focus:border-sxt-primary" defaultValue="ws://localhost:9001" />
              </SettingRow>
            </div>
          )}

          {section === "hardware" && (
            <div>
              <h3 className="text-xl font-bold mb-6">Hardware Info</h3>
              <div className="space-y-3">
                {[
                  ["Mirror Model", "SXT SmartMirror 72×36 1800R"],
                  ["Compute", "NVIDIA Jetson Orin Nano Super (67 TOPS)"],
                  ["Co-processor", "Raspberry Pi 5 (8GB)"],
                  ["Depth Camera", "ZED X Mini (Stereo 4K)"],
                  ["RGB Camera", "Logitech Brio 4K"],
                  ["BCI Headset", "Muse S Gen 2 (4ch EEG)"],
                  ["Display", "Samsung 1800R 72×36\" (3840×2160)"],
                  ["NFC", "ACR122U USB Reader"],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between py-2 border-b border-sxt-border/50">
                    <span className="text-sm text-sxt-muted">{label}</span>
                    <span className="text-sm font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
